import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { brand } from '../config/brand';

type Milestone = { year: string; title: string; description: string };
type Value = { icon: string; title: string; description: string };
type Facility = { title: string; details: string; specs: string[]; image: string };
type Member = { name: string; position: string; experience: string; image?: string };
type Sector = { icon: string; title: string; description: string };

const stats = [
  { value: '15+', label: 'Years of manufacturing' },
  { value: '100+', label: 'Product formulations' },
  { value: '50,000', label: 'Sq ft facility' },
  { value: 'PAN India', label: 'Distribution & export' },
];

const milestones: Milestone[] = [
  {
    year: '2008',
    title: 'Company Founded',
    description:
      'Shine and Aroma Home Care Products was established with a vision to deliver quality contract manufacturing for home, personal, and institutional care.',
  },
  {
    year: '2012',
    title: 'ISO Certification',
    description: 'Achieved ISO 9001:2015 certification, formalising rigorous quality-management systems across production.',
  },
  {
    year: '2015',
    title: 'Facility Expansion',
    description: 'Scaled our manufacturing facility to 50,000 sq ft with advanced, semi-automated production lines.',
  },
  {
    year: '2018',
    title: '100+ Formulations',
    description: 'Crossed the milestone of manufacturing over 100 distinct product formulations across categories.',
  },
  {
    year: '2020',
    title: 'Uclipse Brand Launch',
    description:
      'Launched Uclipse — our dedicated line of concentrated, institutional-strength cleaning chemicals for hospitality, healthcare, and facilities.',
  },
  {
    year: '2022',
    title: 'ZED Bronze Certification',
    description:
      'Recognised under the MSME Sustainable (ZED) Certification Scheme, Government of India, for quality and sustainable manufacturing.',
  },
  {
    year: '2023',
    title: 'Sustainability & Export',
    description: 'Introduced eco-friendly concentrates and export-ready documentation to serve overseas distributors.',
  },
  {
    year: '2026',
    title: 'Upgraded to ZED Silver',
    description:
      'Advanced to ZED Silver under the MSME Sustainable (ZED) Certification Scheme — a higher tier recognising deeper Zero Defect Zero Effect maturity across quality and sustainable manufacturing.',
  },
];

const values: Value[] = [
  {
    icon: '🏆',
    title: 'Quality Excellence',
    description:
      'Every batch is laboratory-validated to meet the highest standards and exceed the expectations of institutional buyers.',
  },
  {
    icon: '🤝',
    title: 'Customer Partnership',
    description: 'We build long-term partnerships through reliable supply, transparent pricing, and responsive service.',
  },
  {
    icon: '🌱',
    title: 'Regenerative Purity',
    description:
      'The Molecular Purity Standard: ultra-pure inputs and concentrated, circular-by-design formats that cut packaging, water and freight — engineered into every litre.',
  },
  {
    icon: '💡',
    title: 'Innovation',
    description: 'Continuous R&D investment lets us formulate higher-yield, safer, and more effective cleaning systems.',
  },
  {
    icon: '⚡',
    title: 'Reliability',
    description: 'Consistent quality delivered on time, every time, backed by clear communication and documentation.',
  },
  {
    icon: '🎯',
    title: 'Integrity',
    description: 'We conduct business with honesty, transparency, and ethical practices across every relationship.',
  },
];

const facilities: Facility[] = [
  {
    title: 'Production Facility',
    details: '50,000 sq ft state-of-the-art manufacturing floor.',
    specs: ['10 production lines', 'Automated packaging', '24/7 operations', 'Climate controlled'],
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=640&h=360&fit=crop&auto=format&q=80',
  },
  {
    title: 'Quality Laboratory',
    details: 'Advanced in-house testing and quality-control laboratory.',
    specs: ['Chemical analysis', 'Microbiological testing', 'Physical testing', 'Stability studies'],
    image: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=640&h=360&fit=crop&auto=format&q=80',
  },
  {
    title: 'Warehouse & Logistics',
    details: 'Modern warehousing built for high-volume institutional dispatch.',
    specs: ['15,000 sq ft storage', 'Inventory management', 'Bulk pack staging', 'Loading docks'],
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=640&h=360&fit=crop&auto=format&q=80',
  },
  {
    title: 'R&D Center',
    details: 'Research and development hub driving product innovation.',
    specs: ['Product formulation', 'Dilution optimisation', 'Prototype development', 'Performance testing'],
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=640&h=360&fit=crop&auto=format&q=80',
  },
];

const team: Member[] = [
  {
    name: 'Poonam Gupta',
    position: 'Chief Executive Officer (CEO)',
    experience: "Visionary leader driving the company's growth",
    image: 'https://beaconaccounting.co.uk/Poonam.png',
  },
  {
    name: 'Priya Sharma',
    position: 'Head of Quality Assurance',
    experience: '15+ years in quality control',
  },
  {
    name: 'Pankaj Boola',
    position: 'Chief Marketing Officer (CMO)',
    experience: 'Driving brand strategy and market growth',
    image: 'https://beaconaccounting.co.uk/Pankaj.png',
  },
  {
    name: 'Sagar Verma',
    position: 'Operations Manager',
    experience: 'Leading manufacturing operations and supply chain',
  },
];

const sectors: Sector[] = [
  {
    icon: '🏨',
    title: 'Hospitality & Tourism',
    description: 'Housekeeping concentrates, guest supplies, and hospitality-grade cleaning systems for hotels and resorts.',
  },
  {
    icon: '🏥',
    title: 'Healthcare & Institutions',
    description: 'Specialised disinfectants and cleaners for hospitals, clinics, and healthcare facilities.',
  },
  {
    icon: '🏢',
    title: 'Facilities & Corporate',
    description: 'Bulk supply for facility-management operators, office complexes, and commercial buildings.',
  },
  {
    icon: '🚗',
    title: 'Automotive & Workshops',
    description: 'High-performance car care and degreasing concentrates for detailing studios and workshops.',
  },
];

function initials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function AboutPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Uclipse',
    description:
      'Uclipse is the institutional cleaning brand of Shine and Aroma Home Care Products — a ZED Silver-certified manufacturer of concentrated cleaning chemicals from Gurugram, Haryana.',
    url: 'https://www.uclipse.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: brand.legalEntity,
      alternateName: 'Uclipse',
      foundingDate: '2008',
      description:
        'Manufacturer of concentrated, institutional-strength cleaning chemicals for hospitality, healthcare, facilities, and automotive care.',
      award: 'ZED Silver Certification under the MSME Sustainable (ZED) Certification Scheme, Government of India',
      hasCredential: {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'certification',
        name: 'ZED Silver Certification',
        recognizedBy: {
          '@type': 'GovernmentOrganization',
          name: 'Ministry of Micro, Small & Medium Enterprises, Government of India',
        },
      },
      numberOfEmployees: { '@type': 'QuantitativeValue', minValue: 50, maxValue: 100 },
      email: brand.email,
      telephone: brand.phone,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: brand.phone,
        email: brand.email,
        contactType: 'sales',
        areaServed: 'IN',
        availableLanguage: ['en', 'hi'],
      },
      knowsAbout: [
        'Contract Manufacturing',
        'Institutional Cleaning Concentrates',
        'Housekeeping Chemicals',
        'Private Label Manufacturing',
        'Automotive Care Products',
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${brand.address.line1}, ${brand.address.line2}`,
        addressLocality: brand.address.city,
        addressRegion: brand.address.state,
        postalCode: brand.address.postalCode,
        addressCountry: 'IN',
      },
    },
  };

  return (
    <>
      <Seo
        title="About Uclipse"
        description="Uclipse is the institutional cleaning brand of Shine and Aroma Home Care Products — a ZED Silver-certified manufacturer of concentrated cleaning chemicals from Gurugram, Haryana. 15+ years, 100+ formulations, PAN-India distribution."
        canonicalUrl="https://www.uclipse.com/about"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-400 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.12'%3E%3Ccircle cx='30' cy='30' r='3'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
        <div className="container-page relative max-w-4xl py-20 text-center md:py-28">
          <span className="inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider backdrop-blur">
            ZED Silver Certified · MSME India
          </span>
          <h1 className="mt-5 font-display text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
            About Uclipse
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-brand-50/90">
            Uclipse is the institutional cleaning brand of <strong>{brand.legalEntity}</strong> — a Gurugram-based
            manufacturer combining 15+ years of contract-manufacturing expertise with high-yield, concentrated
            cleaning technology.
          </p>
          <div className="mx-auto mt-12 grid max-w-3xl grid-cols-2 gap-6 border-t border-white/20 pt-8 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-2xl font-bold md:text-3xl">{s.value}</div>
                <div className="mt-1 text-xs text-brand-50/80 md:text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="container-page grid items-center gap-12 py-16 md:grid-cols-2 md:py-20">
        <div>
          <span className="text-sm font-semibold uppercase tracking-wide text-brand-600">Our Story</span>
          <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 md:text-4xl">
            Built on quality that never compromises
          </h2>
          <div className="mt-6 space-y-4 text-slate-700">
            <p>
              Founded in 2008, Shine and Aroma Home Care Products began as a small manufacturing unit with a big
              vision — to deliver high-quality, affordable care products through contract manufacturing. Over the years
              we have grown into a trusted manufacturer serving clients across multiple industries.
            </p>
            <p>
              Our journey started with a simple belief: quality should never be compromised. That principle has guided
              every expansion, every new formulation, and every client partnership — and it led us to launch Uclipse,
              our institutional-strength line of concentrated cleaning chemicals.
            </p>
            <p>
              With state-of-the-art facilities, an experienced team, and a relentless commitment to innovation, we
              continue to set new standards in institutional and industrial cleaning while staying true to our core
              values.
            </p>
          </div>
        </div>
        <div className="card overflow-hidden p-0">
          <a
            href="/zed-silver.png"
            target="_blank"
            rel="noopener noreferrer"
            className="group block bg-gradient-to-br from-slate-100 via-white to-brand-50 p-4 sm:p-6"
            aria-label="View the full ZED Silver certificate"
          >
            <img
              src="/zed-silver.png"
              alt={`ZED Silver certificate awarded to ${brand.legalEntity} under the MSME Sustainable (ZED) Certification Scheme, Government of India`}
              loading="lazy"
              className="mx-auto w-full max-w-sm rounded-lg shadow-md ring-1 ring-slate-200 transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </a>
          <div className="border-t border-slate-100 bg-slate-50 px-6 py-4 text-sm text-slate-600">
            <p className="font-semibold text-slate-900">ZED Silver Certified — MSME India</p>
            <p className="mt-0.5">
              Awarded to {brand.legalEntity} under the MSME Sustainable (ZED) Certification Scheme, Government of India.
            </p>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <div>
                <dt className="text-slate-400">Certificate No.</dt>
                <dd className="font-medium text-slate-700">07062026_863650</dd>
              </div>
              <div>
                <dt className="text-slate-400">Issued</dt>
                <dd className="font-medium text-slate-700">7 June 2026</dd>
              </div>
              <div>
                <dt className="text-slate-400">Validity</dt>
                <dd className="font-medium text-slate-700">3 years from issue</dd>
              </div>
              <div>
                <dt className="text-slate-400">Udyam Reg No.</dt>
                <dd className="font-medium text-slate-700">{brand.udyam}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-slate-400">Assessment agency</dt>
                <dd className="font-medium text-slate-700">Vexil Business Process Services Pvt Ltd</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Promise */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="container-page grid gap-6 md:grid-cols-3">
          {[
            {
              icon: '🎯',
              title: 'Our Mission',
              body:
                'To provide exceptional manufacturing and cleaning solutions that help our clients operate cleaner, safer, and more cost-effectively — without ever compromising on quality or sustainability.',
            },
            {
              icon: '👁️',
              title: 'Our Vision',
              body:
                'To be the most trusted and innovative cleaning-solutions partner for institutions across India and beyond, recognised for quality, reliability, and customer success.',
            },
            {
              icon: '⭐',
              title: 'Our Promise',
              body:
                'To deliver products that meet your exact specifications — on time and within budget — backed by exceptional service and uncompromising quality standards.',
            },
          ].map((item) => (
            <div
              key={item.title}
              className="group relative overflow-hidden rounded-xl bg-white p-8 text-center shadow-card ring-1 ring-slate-100 transition-all hover:-translate-y-1.5 hover:shadow-lg"
            >
              <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-brand-500 to-accent-green transition-transform group-hover:scale-x-100" />
              <div className="text-4xl">{item.icon}</div>
              <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Core Values */}
      <section className="container-page py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Our Values</h2>
          <p className="mt-3 text-slate-600">The principles that guide our business and relationships.</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {values.map((v) => (
            <div
              key={v.title}
              className="rounded-xl bg-white p-7 shadow-card ring-1 ring-slate-100 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="text-3xl">{v.icon}</div>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Our Journey</h2>
          <p className="mt-3 text-slate-600">Milestones that mark our growth and achievements.</p>
        </div>
        <div className="container-page mt-12 max-w-3xl">
          <div className="relative">
            <span className="absolute left-8 top-0 bottom-0 hidden w-px bg-gradient-to-b from-brand-500 to-accent-green sm:block" />
            <div className="space-y-6">
              {milestones.map((m) => (
                <div key={m.year} className="flex items-start gap-5">
                  <div className="grid h-16 w-16 flex-shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-600 to-brand-400 text-sm font-bold text-white shadow-md">
                    {m.year}
                  </div>
                  <div className="flex-1 rounded-xl bg-white p-6 shadow-card ring-1 ring-slate-100 transition-all hover:translate-x-1 hover:shadow-lg">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{m.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="container-page py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Our Facilities</h2>
          <p className="mt-3 text-slate-600">State-of-the-art infrastructure supporting our manufacturing excellence.</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {facilities.map((f) => (
            <div
              key={f.title}
              className="group card overflow-hidden p-0 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={f.image}
                  alt={f.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{f.details}</p>
                <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700">
                  {f.specs.map((spec) => (
                    <li key={spec} className="flex items-center gap-2">
                      <span className="text-brand-500">▸</span>
                      {spec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Leadership Team */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Leadership Team</h2>
          <p className="mt-3 text-slate-600">Experienced professionals driving our success.</p>
        </div>
        <div className="container-page mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {team.map((member) => (
            <div
              key={member.name}
              className="group overflow-hidden rounded-xl bg-white text-center shadow-card ring-1 ring-slate-100 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="relative h-56 overflow-hidden bg-gradient-to-br from-brand-600 to-brand-400">
                <div className="grid h-full w-full place-items-center font-display text-5xl font-bold text-white/90">
                  {initials(member.name)}
                </div>
                {member.image && (
                  <img
                    src={member.image}
                    alt={member.name}
                    className="absolute inset-0 h-full w-full object-cover object-[center_20%] transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                  />
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold text-slate-900">{member.name}</h3>
                <p className="mt-0.5 text-sm font-medium text-brand-600">{member.position}</p>
                <p className="mt-1.5 text-xs text-slate-500">{member.experience}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sector Experience */}
      <section className="container-page py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Sector Experience</h2>
          <p className="mt-3 text-slate-600">Diverse industry expertise across the institutions we serve.</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {sectors.map((s) => (
            <div
              key={s.title}
              className="flex gap-5 rounded-xl bg-gradient-to-br from-slate-50 to-white p-7 ring-1 ring-slate-100 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-lg bg-brand-50 text-2xl">
                {s.icon}
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-700 to-brand-500 py-16 text-white md:py-20">
        <div className="container-page text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Partner with Uclipse</h2>
          <p className="mx-auto mt-3 max-w-2xl text-brand-50/90">
            Ready to work with a trusted manufacturing partner? Talk to our team about a custom price list for your
            facility or apply to become a regional distributor.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn bg-white text-brand-700 hover:bg-slate-100">
              Get in Touch
            </Link>
            <Link to="/distributors" className="btn bg-accent-green text-white hover:bg-green-600">
              Become a Distributor
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
